import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";
import { isEmpty } from "lodash";

import placeholder from "~/images/favicon.png";

import Image from "~/components/Image";
import BaseLink from "~/components/links/BaseLink";

import { Excerpt, trackKcCardClick } from "~/components/KnowledgeCenterCard";
import { Thumbnail, Author, AuthorDetails } from "~/components/ArticleMetaData";

import { h4 } from "~/styles/typographyStyles";

import transition from "~/utilities/transition";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { formatArticleDate } from "~/utilities/formatDates";

const StyledImage = styled(Image)`
    ${transition({}, "transform")};
    aspect-ratio: 1.57;
`;

const Title = styled.h3`
    ${transition({}, "color")};
    ${h4};
    font-weight: 700;
    margin-bottom: 25px;
`;

const Wrapper = styled.article`
    ${transition({}, "background-color")};
    border-top: 2px solid ${({ theme }) => theme.palette.borderGray};
    padding: 25px;
    position: relative;

    &:hover,
    &:focus {
        background-color: ${({ theme }) =>
            theme.colors.backgroundColor.section};

        ${StyledImage} {
            transform: scale(1.02);
        }

        ${Title} {
            color: ${({ theme }) => theme.palette.teal};
        }
    }
`;

const ArticleLink = styled(BaseLink)`
    color: currentColor;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
    }
`;

const ImageWrapper = styled.div`
    overflow: hidden;
    aspect-ratio: 1.57;
    margin-bottom: 18px;
`;

const StyledAuthor = styled(Author)`
    margin-bottom: 20px;
`;

const StyledThumbnail = styled(Thumbnail)`
    max-width: 55px;
`;

const AuthorName = styled.h4`
    font-weight: 700;
    font-size: 1.8rem;
`;

export const AuthorJobTitle = styled.div`
    font-size: 1.4rem;
`;

const Footer = styled.div`
    display: flex;
    gap: 20px;
    font-size: 1.6rem;
    margin-top: 20px;
`;

const DateRead = styled.div`
    color: ${({ theme }) => theme.palette.grayOne};

    &:first-of-type {
        position: relative;

        &::after {
            content: "";
            position: absolute;
            top: 50%;
            right: -10px;
            transform: translate(50%, -50%);
            width: 3px;
            height: 3px;
            border-radius: 50%;
            background-color: ${({ theme }) => theme.palette.grayOne};
        }
    }
`;

const ThoughtLeadershipCard = ({
    excerpt = "",
    title,
    types = [],
    primaryTopic = null,
    languageCode,
    date,
    image = null,
    author = null,
    readingTime,
    url,
    urlOverride = "",
    openUrlOverrideInANewTab = false,
}) => {
    const link = urlOverride ? urlOverride : url;

    return (
        <Wrapper>
            {image ? (
                <ImageWrapper>
                    <StyledImage {...image} />
                </ImageWrapper>
            ) : null}
            {!isEmpty(author) && (author.name || author.jobTitle) ? (
                <StyledAuthor>
                    {author.thumbnail ? (
                        <StyledThumbnail {...author.thumbnail} />
                    ) : (
                        <StyledThumbnail
                            sourceUrl={placeholder}
                            altText="Author placeholder"
                        />
                    )}
                    <AuthorDetails>
                        {author.name ? (
                            <AuthorName>{author.name}</AuthorName>
                        ) : null}
                        {author.jobTitle ? (
                            <AuthorJobTitle>{author.jobTitle}</AuthorJobTitle>
                        ) : null}
                    </AuthorDetails>
                </StyledAuthor>
            ) : null}
            <Title>
                <ArticleLink
                    to={link}
                    external={!!urlOverride && openUrlOverrideInANewTab}
                    onClick={() =>
                        trackKcCardClick(
                            title,
                            link,
                            primaryTopic,
                            types,
                            languageCode,
                        )
                    }
                >
                    {sanitizeAndParseHTML(title)}
                </ArticleLink>
            </Title>
            {excerpt ? (
                <Excerpt>{sanitizeAndParseHTML(excerpt)}</Excerpt>
            ) : null}
            <Footer>
                <DateRead>{readingTime} min read</DateRead>
                <DateRead>{formatArticleDate(date)}</DateRead>
            </Footer>
        </Wrapper>
    );
};

ThoughtLeadershipCard.propTypes = {
    excerpt: PropTypes.string,
    title: PropTypes.string.isRequired,
    types: PropTypes.arrayOf(PropTypes.shape({})),
    primaryTopic: PropTypes.shape({}),
    date: PropTypes.string.isRequired,
    image: PropTypes.shape({}),
    author: PropTypes.shape({}),
    url: PropTypes.string.isRequired,
    readingTime: PropTypes.number.isRequired,
    urlOverride: PropTypes.string,
    openUrlOverrideInANewTab: PropTypes.bool,
};

export default ThoughtLeadershipCard;
